import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Add, Edit, Delete, Inventory, CheckCircle, Cancel } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Product } from "../../../../types";
import ProductImage from "../../../../components/ProductImage";

interface ProductsTabProps {
  products: Product[];
  loading: boolean;
  onDelete: (productId: string) => Promise<void>;
}

const ProductsTab: React.FC<ProductsTabProps> = ({ products, loading, onDelete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 2, sm: 0 },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontSize: { xs: "1.25rem", sm: "1.5rem" } }}>
          {t("dashboard.myProducts")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/admin/products/new")}
          fullWidth
          sx={{
            background: "linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)",
            boxShadow: "0 2px 8px rgba(0,212,255,0.2)",
            width: { xs: "100%", sm: "auto" },
          }}
        >
          {t("dashboard.addProduct")}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : products.length === 0 ? (
        <Alert severity="info">{t("dashboard.noProductsYet")}</Alert>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 3,
          }}
        >
          {products.map((product) => (
            <Box key={product.id}>
              <Card
                sx={{
                  height: "100%",
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "divider",
                  transition: "all 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 4px 12px rgba(0,212,255,0.15)",
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      mb: 2,
                      height: 150,
                      overflow: "hidden",
                      borderRadius: "8px",
                      bgcolor: "rgba(255,255,255,0.02)",
                    }}
                  >
                    {product.images && product.images.length > 0 ? (
                      <ProductImage
                        src={product.images[0]}
                        alt={product.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <Inventory
                          sx={{
                            fontSize: 48,
                            color: "rgba(255,255,255,0.2)",
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  <Typography variant="h6" gutterBottom noWrap>
                    {product.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2, height: 40, overflow: "hidden" }}
                  >
                    {product.description}
                  </Typography>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      label={product.category}
                      size="small"
                      sx={{
                        bgcolor: "rgba(0,212,255,0.2)",
                        color: "#00d4ff",
                      }}
                    />
                    <Chip
                      label={product.condition}
                      size="small"
                      sx={{
                        bgcolor: "rgba(0,255,136,0.2)",
                        color: "#00ff88",
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ color: "#00d4ff" }}>
                      €{product.zetta_price?.toFixed(2) || product.price.toFixed(2)}
                    </Typography>
                    <Chip
                      label={product.status}
                      size="small"
                      icon={
                        product.status === "available" ? (
                          <CheckCircle fontSize="small" />
                        ) : (
                          <Cancel fontSize="small" />
                        )
                      }
                      sx={{
                        bgcolor:
                          product.status === "available"
                            ? "rgba(0,255,136,0.2)"
                            : "rgba(255,51,102,0.2)",
                        color:
                          product.status === "available" ? "#00ff88" : "#ff3366",
                      }}
                    />
                  </Box>

                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                      sx={{ color: "#00d4ff" }}
                    >
                      {t("common.edit")}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Delete />}
                      onClick={() => onDelete(product.id)}
                      sx={{ color: "#ff3366" }}
                    >
                      {t("common.delete")}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProductsTab;